/**
 * 阶段流程图组件
 * 使用 Mermaid 渲染流程图，支持查看和编辑
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input, Space, message } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import mermaid from 'mermaid';

const { TextArea } = Input;

interface PhaseFlowDiagramProps {
    phaseName: string;
    flowDiagram?: string;
    onUpdate: (diagram: string) => void;
}

// 初始化 Mermaid
if (typeof window !== 'undefined') {
    mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        fontFamily: 'Arial, sans-serif'
    });
}

export const PhaseFlowDiagram: React.FC<PhaseFlowDiagramProps> = ({
    phaseName,
    flowDiagram,
    onUpdate
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingContent, setEditingContent] = useState('');
    const [renderedDiagram, setRenderedDiagram] = useState('');
    const diagramRef = useRef<HTMLDivElement>(null);

    const defaultDiagram = `graph LR
    A[开始] --> B[执行]
    B --> C[完成]`;

    const currentDiagram = flowDiagram || defaultDiagram;

    // 渲染 Mermaid 图表
    const renderDiagram = async (content: string) => {
        try {
            const { svg } = await mermaid.render(`mermaid-${Date.now()}`, content);
            setRenderedDiagram(svg);
        } catch (error) {
            console.error('Mermaid render error:', error);
            message.error('流程图渲染失败，请检查语法');
        }
    };

    useEffect(() => {
        if (isModalOpen && !isEditing) {
            renderDiagram(currentDiagram);
        }
    }, [isModalOpen, currentDiagram, isEditing]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setIsEditing(false);
    };

    const handleStartEdit = () => {
        setEditingContent(currentDiagram);
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        // 先尝试渲染，验证语法
        try {
            await mermaid.render(`mermaid-test-${Date.now()}`, editingContent);
            onUpdate(editingContent);
            setIsEditing(false);
            message.success('流程图已更新');
            renderDiagram(editingContent);
        } catch (error) {
            message.error('流程图语法错误，请检查后重试');
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingContent('');
    };

    return (
        <>
            <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={handleOpenModal}
                size="small"
            >
                查看流程图
            </Button>

            <Modal
                title={`${phaseName} - 运营流程图`}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setIsEditing(false);
                    setEditingContent('');
                }}
                width={800}
                footer={isEditing ? [
                    <Button key="cancel" onClick={handleCancelEdit}>
                        取消
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveEdit}>
                        保存
                    </Button>
                ] : [
                    <Button key="edit" icon={<EditOutlined />} onClick={handleStartEdit}>
                        编辑
                    </Button>,
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        关闭
                    </Button>
                ]}
            >
                {isEditing ? (
                    <div>
                        <div style={{ marginBottom: 12 }}>
                            <Space>
                                <span style={{ fontSize: 13, color: '#666' }}>
                                    💡 提示：使用 Mermaid 语法定义流程图
                                </span>
                                <a
                                    href="https://mermaid.js.org/syntax/flowchart.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: 13 }}
                                >
                                    查看语法文档
                                </a>
                            </Space>
                        </div>
                        <TextArea
                            value={editingContent}
                            onChange={e => setEditingContent(e.target.value)}
                            rows={12}
                            placeholder="输入 Mermaid 流程图代码..."
                            style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                    </div>
                ) : (
                    <div
                        ref={diagramRef}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 300,
                            padding: 20,
                            background: '#f5f5f5',
                            borderRadius: 4
                        }}
                        dangerouslySetInnerHTML={{ __html: renderedDiagram }}
                    />
                )}
            </Modal>
        </>
    );
};
